import { Tooltip } from 'antd'
import { CiFileOn } from 'react-icons/ci'
import { MdCheck, MdOutlineInfo, MdOutlineSupportAgent, MdOutlineTaskAlt } from 'react-icons/md'

import { GetResourceTask } from '@/Core/Tasks/Resources/GetResourceTask'
import { cx } from '@/Ship/Helpers/cssClass'

import { formatTime } from '../Helpers/formatTime'

import styles from './NotificationDrawer.module.css'

import type { Notification } from '../Stores/NotificationManager'
import type { CSSProperties, ComponentType } from 'react'

interface NotificationListItemProps {
    item: Notification
    onClick?: (item: Notification) => void
    /** Позначити прочитаним без відкриття картки (кнопка при наведенні) */
    onMarkRead?: (item: Notification) => void
}

/** Підпис, колір і іконка типу сповіщення */
const TYPE_META: Record<string, { label: string; color: string; icon: ComponentType<{ size?: number }> }> = {
    dispatcher: { label: 'Диспетчер', color: 'var(--as-accent)', icon: MdOutlineSupportAgent },
    system: { label: 'Система', color: '#4a9eff', icon: MdOutlineInfo },
    task: { label: 'Завдання', color: '#52c41a', icon: MdOutlineTaskAlt },
}

/**
 * Рядок сповіщення: плитка з іконкою типу, заголовок, текст, час.
 * Непрочитане має смужку ліворуч і підсвітку; при наведенні з'являється
 * кнопка «Позначити прочитаним».
 */
export const NotificationListItem = ({ item, onClick, onMarkRead }: NotificationListItemProps) => {
    const resource = item.entityCode ? GetResourceTask(item.entityCode) : undefined
    const meta = TYPE_META[item.type] ?? TYPE_META['system']!
    const Icon =
        (resource?.['pageConfig']?.icon as ComponentType<{ size?: number }> | undefined) ?? meta.icon ?? CiFileOn
    const isUnread = !item.isReaded
    const title = item.title?.trim()
    const message = item.message?.trim()

    return (
        <div
            role="button"
            tabIndex={0}
            className={`${cx(styles, 'item')} ${isUnread ? cx(styles, 'itemUnread') : ''}`}
            style={{ '--notification-color': meta.color } as CSSProperties}
            onClick={() => onClick?.(item)}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onClick?.(item)
                }
            }}
        >
            <div className={cx(styles, 'itemIcon')} aria-hidden="true">
                <Icon size={20} />
            </div>

            <div className={cx(styles, 'itemBody')}>
                <div className={cx(styles, 'itemTop')}>
                    <span className={cx(styles, 'itemType')}>{meta.label}</span>
                    {item.createdAt && <span className={cx(styles, 'itemTime')}>{formatTime(item.createdAt)}</span>}
                </div>
                {title && <div className={cx(styles, 'itemTitle')}>{title}</div>}
                {message && message !== title && <div className={cx(styles, 'itemMessage')}>{message}</div>}
            </div>

            {isUnread && onMarkRead && (
                <Tooltip title="Позначити прочитаним" placement="left">
                    <button
                        type="button"
                        className={cx(styles, 'itemMarkRead')}
                        aria-label="Позначити прочитаним"
                        onClick={(event) => {
                            event.stopPropagation()
                            onMarkRead(item)
                        }}
                    >
                        <MdCheck />
                    </button>
                </Tooltip>
            )}
        </div>
    )
}
